import React from "react";
import { createBaseBlock } from "./BaseBlock";
import { DigestDataLinks } from "pages/[orgId]/teams/components/ReportLinks";
import { AnnotatedDeepLink, DigestV2State } from "pages/api/deep-links/types";
import { Card, List, Table, Typography, Space, theme } from "antd";
import { useOrg } from "src/hooks/useOrg";
import { useApplicationContext } from "src/hooks/useApplication";
import {
  FileTextOutlined,
  LinkOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import { MenuItemType } from "antd/lib/menu/hooks/useItems";

// Resources Block Recipe
const resourcesRecipe = {
  id: "resources-block",
  name: "Resources Block",
  globalKey: [],
  prefix: "",
  format: {
    type: "json",
    fields: [],
    constraints: [],
  },
  validationRules: [],
};

// Resources Block Parser
function resourcesParser(data: any) {
  return {
    data: data,
  };
}

// Resources Block Variants
const resourcesVariants = [
  {
    id: "list",
    name: "List View",
    description: "Simple list view of resources",
    defaultSettings: {
      showIcons: true,
      showDescriptions: true,
    },
  },
  {
    id: "table",
    name: "Table View",
    description: "Structured table view of resources",
    defaultSettings: {
      showType: true,
      showDate: true,
    },
  },
  {
    id: "cards",
    name: "Card View",
    description: "Visual card-based view of resources",
    defaultSettings: {
      showPreview: true,
      showTags: true,
    },
  },
];

// List View Renderer
const ListViewRenderer: React.FC<{ items: MenuItemType[] }> = ({ items }) => {
  const { token } = theme.useToken();
  const app = useApplicationContext();
  const orgId = useOrg().id!;

  return (
    <List
      dataSource={items}
      renderItem={(item) => (
        <List.Item>
          <Space style={{ width: "100%" }}>
            <div
              key={`${item.label}-link`}
              onClick={() => {
                if (item.key) {
                  app.appendTab(item.label as string, `/${orgId}${item.key}`, {
                    open: true,
                  });
                }
              }}
              style={{ cursor: "pointer" }}
            >
              {item.label}
            </div>
          </Space>
        </List.Item>
      )}
    />
  );
};

// Table View Renderer
const TableViewRenderer: React.FC<{ items: MenuItemType[] }> = ({ items }) => {
  const { token } = theme.useToken();
  const app = useApplicationContext();
  const orgId = useOrg().id!;

  return (
    <Table
      dataSource={items}
      columns={[
        {
          title: "Resource",
          dataIndex: "label",
          key: "label",
          render: (text, record) => (
            <Space>
              <FileTextOutlined style={{ color: token.colorPrimary }} />
              <div
                onClick={() => {
                  if (record.key) {
                    app.appendTab(text as string, `/${orgId}${record.key}`, {
                      open: true,
                    });
                  }
                }}
                style={{ cursor: "pointer" }}
              >
                {text}
              </div>
            </Space>
          ),
        },
        {
          title: "Type",
          dataIndex: "type",
          key: "type",
          render: () => <LinkOutlined />,
        },
      ]}
      pagination={false}
    />
  );
};

// Card View Renderer
const CardViewRenderer: React.FC<{ items: MenuItemType[] }> = ({ items }) => {
  const { token } = theme.useToken();
  const app = useApplicationContext();
  const orgId = useOrg().id!;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
        gap: "16px",
      }}
    >
      {items.map((item: MenuItemType, index: number) => (
        <Card
          key={index}
          hoverable
          style={{ height: "100%" }}
          bodyStyle={{ padding: "12px" }}
          onClick={() => {
            if (item.key) {
              app.appendTab(item.label as string, `/${orgId}${item.key}`, {
                open: true,
              });
            }
          }}
        >
          <Space direction="vertical" style={{ width: "100%" }}>
            <Space style={{ width: "100%" }}>
              <Typography.Text strong>{item.label}</Typography.Text>
            </Space>
            <Typography.Text type="secondary" style={{ fontSize: "12px" }}>
              Click to explore
            </Typography.Text>
          </Space>
        </Card>
      ))}
    </div>
  );
};

// Resources Block Renderer
function ResourcesBlockRenderer(props: any) {
  const { data, variant, link } = props;
  const orgId = useOrg().id!;

  const dataLinks = DigestDataLinks(orgId, link, [], false);
  if (!dataLinks.items || dataLinks.items.length === 0) return null;

  const validItems = dataLinks.items.filter(
    (item): item is MenuItemType =>
      item !== null &&
      "label" in item &&
      (!("disabled" in item) || !item.disabled)
  );

  const renderers = {
    list: ListViewRenderer,
    table: TableViewRenderer,
    cards: CardViewRenderer,
  };

  const SelectedRenderer = renderers[variant as keyof typeof renderers];
  return (
    <div className="vzbl-panel-container">
      <Typography.Title level={4} className="vzbl-header-4">
        Reference Data
      </Typography.Title>
      <div className="vzbl-panel-shadow vzbl-panel-overlay">
        {SelectedRenderer ? <SelectedRenderer items={validItems} /> : null}
      </div>
    </div>
  );
}

// Create Resources Block
const resourcesBlock = createBaseBlock({
  id: "resources",
  name: "Resources Block",
  description: "Displays additional resources and links",
  recipe: resourcesRecipe,
  variants: resourcesVariants,
  parser: resourcesParser,
  renderer: ResourcesBlockRenderer,
});

// Export the block for direct use
export { resourcesBlock };
